import type React from "react";
import type { ReactNode } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import styled from "styled-components";

import { Notification } from "shared/atoms/Notification";

interface ProfileRowProps {
  label: string;
  value: ReactNode;
  tooltip?: string;
  errorMessage?: string;
  dataTestId?: string;
  labelTestId?: string;
}

export const ProfileRow: React.FC<ProfileRowProps> = ({
  label,
  value,
  tooltip,
  errorMessage,
  dataTestId,
  labelTestId,
}) => {
  return (
    <Container data-testid={dataTestId}>
      <LabelRow>
        <Typography variant="subtitle1" data-testid={labelTestId}>
          {label}
        </Typography>
        {tooltip && (
          <TooltipWrapper>
            <FontAwesomeIcon icon={faInfoCircle} />
            <TooltipText>{tooltip}</TooltipText>
          </TooltipWrapper>
        )}
      </LabelRow>
      <ValueWrapper>{value}</ValueWrapper>
      {errorMessage && (
        <Notification variant="danger" style={{ margin: "10px 0" }}>
          {errorMessage}
        </Notification>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-bottom: ${({ theme }) => theme.semantic.container.border};
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ValueWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;

  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.theme.colors.redesign.db60};
  }

  &:hover > div {
    visibility: visible;
  }
`;

const TooltipText = styled.div`
  visibility: hidden;
  ${props => ({ ...props.theme.font.caption })}
  background-color: ${props => props.theme.colors.greys.silver};
  color: black;
  border-radius: 6px;
  padding: ${props => props.theme.spacing.S_10};
  white-space: normal;
  position: absolute;
  z-index: 10;
  width: 300px;
  top: 110%;
  right: 0;
  ${props => props.theme.belowLargeBreakpoint} {
    left: 40%;
    transform: translateX(-100%);
    width: 200px;
  }
`;
